import clsx from "clsx";
import { useWallet, useConnection } from "@solana/wallet-adapter-react";
import { useMemo, useState } from "react";
import { PhantomWalletAdapter } from "@solana/wallet-adapter-phantom";
import { SolflareWalletAdapter } from "@solana/wallet-adapter-solflare";
import { BaseWalletAdapter, WalletReadyState } from "@solana/wallet-adapter-base";
import { SolanaMobileWalletAdapter, SolanaMobileWalletAdapterWalletName } from "@solana-mobile/wallet-adapter-mobile";
import { useAppDispatch } from "@axvdex/state";
import { Wallet, connectWalletWithDispatch } from "@axvdex/state/wallet/walletThunks";
import { WHITE_LIST_PERSISTED_STATE_KEYS, persistState } from "@axvdex/state/persist";
import useLanguage from "@axvdex/hooks/useLanguage";
import { hasPermitStored } from "@axvdex/utils/localStorage";
import { connectSolanaWalletWithDispatch } from "@axvdex/state/wallet/solana/walletThunks";
import { ReactComponent as LogoAstro } from "../assets/icons/icn-astrovault.svg";
import { ReactComponent as LogoBackpack } from "../assets/logos/logo-wallet-backpack.svg";
import LogoBackpackIcon from "../assets/logos/logo-wallet-backpack.png";
import LogoGlowIcon from "../assets/logos/logo-wallet-glow.png";
import LogoSolflareIcon from "../assets/logos/logo-wallet-solflare.png";
import { ReactComponent as LogoCosmostation } from "../assets/logos/logo-wallet-cosmostation.svg";
import { ReactComponent as LogoPhantom } from "../assets/logos/logo-wallet-phantom.svg";
import { ReactComponent as LogoPhantomLong } from "../assets/logos/logo-wallet-phantom-long.svg";
import { ReactComponent as LogoSolflare } from "../assets/logos/logo-wallet-solflare.svg";
import styles from "../styles/WalletConnectListItems.module.scss";
import Button from "./common/Button";
import { useGlobalModalContext } from "./../providers/GlobalModalProvider";

function WalletConnectListItems({
  isLoading,
  setIsLoading,
}: {
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  if (process.env.REACT_APP_SOLANA_ENABLED === "1") {
    return <SolanaWalletConnectListItems isLoading={isLoading} setIsLoading={setIsLoading} />;
  }

  return <DefaultWalletConnectListItems isLoading={isLoading} setIsLoading={setIsLoading} />;
}

function DefaultWalletConnectListItems({
  isLoading,
  setIsLoading,
}: {
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const { i18 } = useLanguage();
  const dispatch = useAppDispatch();
  const { hideModal, showModal } = useGlobalModalContext();

  return (
    <ul className={clsx(styles.walletConnectList, "walletConnectList")}>
      <li className="walletConnectListItem">
        <Button
          iconPlacement="left"
          icon={<LogoCosmostation />}
          text={i18("Cosmostation", "walletConnectList.btnConnectCosmostation.text")}
          title={i18("Connect Wallet to Cosmostation", "walletConnectList.btnConnectCosmostation.title")}
          disabled={isLoading}
          onClick={async () => {
            persistState(WHITE_LIST_PERSISTED_STATE_KEYS.connectedWallet, "cosmostation");
            setIsLoading(true);
            const walletConnect: Wallet | null = (await dispatch(connectWalletWithDispatch({ showModal })))
              .payload as Wallet | null;
            if (walletConnect) {
              hideModal();
              if (
                !hasPermitStored({
                  walletAddress: walletConnect.address,
                })
              )
                showModal("permitAuthenticationModal");
            }
            setIsLoading(false);
          }}
        />
        <Button
          iconPlacement="left"
          icon={<i className="feather icon-download-cloud" />}
          text={i18("Download", "walletConnectList.btnDownloadCosmostation.text")}
          btnVariant="link"
          btnColor="gradientText"
          title={i18("Download Cosmostation App", "walletConnectList.btnDownloadCosmostation.title")}
          btnSize="sm"
          onClick={() => {
            window.open("https://cosmostation.io/wallet/#extension", "_blank", "noopener, noreferrer");
          }}
        />
      </li>
    </ul>
  );
}

function SolanaWalletConnectListItems({
  isLoading,
  setIsLoading,
}: {
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const { i18 } = useLanguage();
  const { hideModal } = useGlobalModalContext();
  const dispatch = useAppDispatch();
  const { connection } = useConnection();
  const { wallets, select } = useWallet();

  // Detect all the wallets =================================================================================
  // NOTE: Pahntom & Solflare have distinct class so it's easy to detect them
  const [phantomWallet] = useState(new PhantomWalletAdapter());
  const [solflareWallet] = useState(new SolflareWalletAdapter());
  // NOTE: For mobile wallets, we need to check if the wallet has been detect automatically, if so, it appears in the list
  const androidMobileWalletAdapter = useMemo(() => {
    const existingMobileWalletAdapter = wallets.find(
      wallet => wallet.adapter.name === SolanaMobileWalletAdapterWalletName
    );
    if (existingMobileWalletAdapter) {
      return existingMobileWalletAdapter;
    }
  }, [wallets, connection]);

  // INFO: All new wallet implement a standart, which allow them to be detected by the wallet adapter. They all implement the adapter interface
  // - Glow wallet (glow.app)
  const glowWalletAdapter = useMemo(() => {
    const existingMobileWalletAdapter = wallets.find(wallet => wallet.adapter.name === "Glow");
    if (existingMobileWalletAdapter) {
      return existingMobileWalletAdapter;
    }
  }, [wallets, connection]);

  // - Backpack wallet (backpack.app)
  const backpackWalletAdapter = useMemo(() => {
    const existingMobileWalletAdapter = wallets.find(wallet => wallet.adapter.name === "Backpack");
    if (existingMobileWalletAdapter) {
      return existingMobileWalletAdapter;
    }
  }, [wallets, connection]);

  // Handle wallet connect process to propagate the wallet to the app ======================================
  const handleWalletConnectDispatch = async (walletAdapter: BaseWalletAdapter) => {
    setIsLoading(true);
    try {
      const walletConnect: Wallet | null = (await dispatch(connectSolanaWalletWithDispatch({ walletAdapter })))
        .payload as Wallet | null;
      if (walletConnect) {
        hideModal();
        select(walletAdapter.name);
      }
    } catch (error) {
      console.error("Failed to connect", error);
    } finally {
      setIsLoading(false);
    }
  };

  // If in a In-Wallet-App-Browser on a phone, show the normal connect button (same protocol as desktop) =====
  const onMobileInWalletAppBrowser =
    (androidMobileWalletAdapter || isOniOS()) &&
    (phantomWallet.readyState === WalletReadyState.Installed ||
      solflareWallet.readyState === WalletReadyState.Installed ||
      (backpackWalletAdapter && backpackWalletAdapter.adapter.readyState === WalletReadyState.Installed));

  // Android Browser | Use the Wallet Standart (MobileAdapter) ===============================================
  if (androidMobileWalletAdapter && !onMobileInWalletAppBrowser) {
    return (
      <>
        <Button
          style={{ width: "100%" }}
          extraClassName="btnMobile"
          iconPlacement="left"
          btnColor="gradient"
          icon={<LogoAstro />}
          text={i18("Select your Wallet", "walletConnectList.btnConnectPhantom.text")}
          title={i18("Connect Wallet", "walletConnectList.btnConnectPhantom.title")}
          disabled={isLoading}
          onClick={async () => {
            const walletAdapter = androidMobileWalletAdapter.adapter as SolanaMobileWalletAdapter;
            await handleWalletConnectDispatch(walletAdapter);
          }}
        />
        <ul className={clsx(styles.walletConnectListAndroid, "walletConnectListAndroid")}>
          <li className="walletConnectListItem">
            <LogoPhantomLong style={{ height: "22px", width: "100px" }} />
            <Button
              iconPlacement="left"
              icon={<i className="feather icon-download-cloud" />}
              text={i18("Download", "walletConnectList.btnDownloadPhantom.text")}
              title={i18("Download Phantom App", "walletConnectList.btnDownloadPhantom.title")}
              btnVariant="link"
              btnColor="gradientText"
              btnSize="sm"
              onClick={() => {
                window.open("https://phantom.app/", "_blank", "noopener, noreferrer");
              }}
            />
          </li>
          <li className="walletConnectListItem">
            <LogoSolflare style={{ height: "30px", width: "100px" }} />
            <Button
              iconPlacement="left"
              icon={<i className="feather icon-download-cloud" />}
              text={i18("Download", "walletConnectList.btnDownloadSolflare.text")}
              title={i18("Download Solflare App", "walletConnectList.btnDownloadSolflare.title")}
              btnVariant="link"
              btnColor="gradientText"
              btnSize="sm"
              onClick={() => {
                window.open("https://solflare.com/", "_blank", "noopener, noreferrer");
              }}
            />
          </li>
        </ul>
      </>
    );
  }

  // If on iOS safari, show the glow button ===============================================================
  if (isOniOS() && !onMobileInWalletAppBrowser) {
    return (
      <ul className={clsx(styles.walletConnectList, "walletConnectList")}>
        {/* iOS: Only glow is supported ATM | On Safari */}
        <li className="walletConnectListItem">
          <Button
            extraClassName="btnMobile"
            iconPlacement="left"
            icon={<img src={LogoGlowIcon} style={{ width: 25, borderRadius: "2px" }} />}
            text={i18("Connect Glow", "walletConnectList.btnConnectPhantom.text")}
            title={i18("Connect Wallet", "walletConnectList.btnConnectPhantom.title")}
            disabled={isLoading || !glowWalletAdapter}
            onClick={async () => {
              const walletAdapter = glowWalletAdapter.adapter as SolanaMobileWalletAdapter;
              await handleWalletConnectDispatch(walletAdapter);
            }}
          />
          <Button
            iconPlacement="left"
            icon={<i className="feather icon-download-cloud" />}
            text={i18("Download", "walletConnectList.btnDownloadPhantom.text")}
            title={i18("Download Phantom App", "walletConnectList.btnDownloadPhantom.title")}
            btnVariant="link"
            btnColor="gradientText"
            btnSize="sm"
            onClick={() => {
              window.open("https://glow.app/download", "_blank", "noopener, noreferrer");
            }}
          />
        </li>
      </ul>
    );
  }

  // On Desktop ====================================================================================
  // - Or if one of the provider are detected, meaning the user is in the browser of one of the wallets
  return (
    <ul className={clsx(styles.walletConnectList, "walletConnectList")}>
      <li className="walletConnectListItem">
        <Button
          extraClassName="btnPhantom"
          iconPlacement="left"
          icon={<LogoPhantom />}
          text={i18("Connect Phantom", "walletConnectList.btnConnectPhantom.text")}
          title={i18("Connect Wallet to Phantom", "walletConnectList.btnConnectPhantom.title")}
          disabled={isLoading || phantomWallet.readyState !== WalletReadyState.Installed}
          onClick={async () => {
            await handleWalletConnectDispatch(phantomWallet);
          }}
        />
        <Button
          iconPlacement="left"
          icon={<i className="feather icon-download-cloud" />}
          text={i18("Download", "walletConnectList.btnDownloadPhantom.text")}
          title={i18("Download Phantom App", "walletConnectList.btnDownloadPhantom.title")}
          btnVariant="link"
          btnColor="gradientText"
          btnSize="sm"
          onClick={() => {
            window.open("https://phantom.app/", "_blank", "noopener, noreferrer");
          }}
        />
      </li>
      <li className="walletConnectListItem">
        <Button
          extraClassName="btnSolflare"
          iconPlacement="left"
          icon={<img src={LogoSolflareIcon} style={{ width: 25 }} />}
          text={i18("Connect Solflare", "walletConnectList.btnConnectSolflare.text")}
          title={i18("Connect Wallet to Solflare", "walletConnectList.btnConnectSolflare.title")}
          disabled={isLoading || solflareWallet.readyState !== WalletReadyState.Installed}
          onClick={async () => {
            await handleWalletConnectDispatch(solflareWallet);
          }}
        />
        <Button
          iconPlacement="left"
          icon={<i className="feather icon-download-cloud" />}
          text={i18("Download", "walletConnectList.btnDownloadSolflare.text")}
          title={i18("Download Solflare App", "walletConnectList.btnDownloadSolflare.title")}
          btnVariant="link"
          btnColor="gradientText"
          btnSize="sm"
          onClick={() => {
            window.open("https://solflare.com/", "_blank", "noopener, noreferrer");
          }}
        />
      </li>
      <li className="walletConnectListItem">
        <Button
          extraClassName="btnMobile"
          iconPlacement="left"
          icon={<img src={LogoGlowIcon} style={{ width: 25, borderRadius: "2px" }} />}
          text={i18("Connect Glow", "walletConnectList.btnConnectPhantom.text")}
          title={i18("Connect Wallet", "walletConnectList.btnConnectPhantom.title")}
          disabled={isLoading || !glowWalletAdapter}
          onClick={async () => {
            const walletAdapter = glowWalletAdapter.adapter as SolanaMobileWalletAdapter;
            await handleWalletConnectDispatch(walletAdapter);
          }}
        />
        <Button
          iconPlacement="left"
          icon={<i className="feather icon-download-cloud" />}
          text={i18("Download", "walletConnectList.btnDownloadPhantom.text")}
          title={i18("Download Phantom App", "walletConnectList.btnDownloadPhantom.title")}
          btnVariant="link"
          btnColor="gradientText"
          btnSize="sm"
          onClick={() => {
            window.open("https://glow.app/download", "_blank", "noopener, noreferrer");
          }}
        />
      </li>
      <li className="walletConnectListItem">
        <Button
          extraClassName="btnMobile"
          iconPlacement="left"
          icon={<img src={LogoBackpackIcon} style={{ width: 15, marginLeft: 5, marginRight: 5 }} />}
          text={i18("Connect Backpack", "walletConnectList.btnConnectBackpack.text")}
          title={i18("Connect Wallet", "walletConnectList.btnConnectBackpack.title")}
          disabled={isLoading || !backpackWalletAdapter}
          onClick={async () => {
            const walletAdapter = backpackWalletAdapter.adapter as SolanaMobileWalletAdapter;
            await handleWalletConnectDispatch(walletAdapter);
          }}
        />
        <Button
          iconPlacement="left"
          icon={<i className="feather icon-download-cloud" />}
          text={i18("Download", "walletConnectList.btnDownloadPhantom.text")}
          title={i18("Download Phantom App", "walletConnectList.btnDownloadPhantom.title")}
          btnVariant="link"
          btnColor="gradientText"
          btnSize="sm"
          onClick={() => {
            window.open("https://backpack.app/download", "_blank", "noopener, noreferrer");
          }}
        />
      </li>
    </ul>
  );
}

export default WalletConnectListItems;

function isOniOS() {
  return (
    ["iPad Simulator", "iPhone Simulator", "iPod Simulator", "iPad", "iPhone", "iPod"].includes(navigator.platform) ||
    // iPad on iOS 13 detection
    (navigator.userAgent.includes("Mac") && "ontouchend" in document)
  );
}
